import { SupportedPaymentMethods, CaptureMode } from '../constants';

import type { Settings, CaptureModeType, PaymentMethodsType, PaymentMethodsConfig } from '../@types';

/**
 * Detect if settings contains given payment method.
 */
const hasPaymentMethod = (
  paymentMethods: (keyof PaymentMethodsConfig)[],
  settings: Settings,
  method: PaymentMethodsType,
) => paymentMethods.includes(method)
  && method in settings.paymentMethodConfigurations
  && settings.paymentMethodConfigurations[method];

/**
 * Generate object containing config data for all accepted payment methods.
 * Narrows configuration down to accepted payment methods, and extracts data for them.
 */
export const generatePaymentMethodsConfiguration = (
  settings: Settings,
  amount?: number | null,
  capture: CaptureModeType = CaptureMode.CHARGE,
): {
  [key in PaymentMethodsType]?: object;
} => {
  const paymentMethodKeys = Object.keys(settings.paymentMethodConfigurations) as PaymentMethodsType[];

  /**
   * Calculate accepted payment types.
   */
  const paymentMethods = paymentMethodKeys.reduce((acc, configType) => {
    const configData = settings.paymentMethodConfigurations[configType];

    // If no constraints are present, accept this payment type.
    const constraints = configData?.constraints;
    if (!constraints) {
      acc.push(configType);
      return acc;
    }

    const minAmount = constraints?.minAmount;
    const maxAmount = constraints?.maxAmount;

    // If min & max constraints don't provide a valid range, accept this payment type.
    if (maxAmount <= minAmount) {
      acc.push(configType);
      return acc;
    }

    // If amount is between min & max constraints, accept this payment type.
    if (typeof amount === 'number' && amount >= minAmount && amount <= maxAmount) acc.push(configType);

    // If no preceding conditions match, reject this payment type.
    return acc;
  }, [] as PaymentMethodsType[]);

  return {
    ...(paymentMethods.includes(SupportedPaymentMethods.CARD) && {
      card: {
        billingConfig: {
          ...(settings.billingAddressFields || {}),
        },
        // TODO: must be configurable
        contactInformationConfig: {
          firstName: { required: true },
          lastName: { required: true },
        },
      },
    }),
    ...(paymentMethods.includes(SupportedPaymentMethods.OXXO)
      && capture === CaptureMode.CHARGE && {
      oxxo: {},
    }),
    ...(paymentMethods.includes(SupportedPaymentMethods.AFFIRM)
      && capture === CaptureMode.CHARGE && {
      affirm: {},
    }),
    ...(paymentMethods.includes(SupportedPaymentMethods.APPLEPAY)
      && capture === CaptureMode.CHARGE && {
      apple_pay: {},
    }),
    ...(paymentMethods.includes(SupportedPaymentMethods.GOOGLEPAY)
      && capture === CaptureMode.CHARGE && {
      google_pay: {},
    }),
    ...(hasPaymentMethod(paymentMethods, settings, SupportedPaymentMethods.PAYPAL as PaymentMethodsType)
      && capture === CaptureMode.CHARGE && {
      pay_pal: { ...settings.paymentMethodConfigurations.pay_pal },
    }),
  };
};
